import { OWWindow } from './ow-window';
import { OWUtils } from '../ow-utils';
import {
  OWWindowsListener,
  OWWindowsListenerDelegate,
} from './ow-windows-listener';
import { OWWindowAnchorTypes } from './ow-window-anchor-types';
import Display = overwolf.utils.Display;

// -----------------------------------------------------------------------------
type onScreenPropertyChangedEvent =
  overwolf.windows.onScreenPropertyChangedEvent;

// -----------------------------------------------------------------------------
export interface OWWindowScreenAnchorOptions {
  window: OWWindow;
  anchorType: OWWindowAnchorTypes;
  isTopMost: boolean;
}

// -----------------------------------------------------------------------------
// Anchors a given window to an area of the user's screen
export class OWWindowScreenAnchor implements OWWindowsListenerDelegate {
  private readonly window: OWWindow;
  private readonly owWindowListener: OWWindowsListener;
  private readonly anchorType: OWWindowAnchorTypes;

  private mainMonitorId = '';
  private mainMonitor: Display;
  private isTopMost: boolean;

  // ---------------------------------------------------------------------------
  constructor(owWindowScreenAnchorOptions: OWWindowScreenAnchorOptions) {
    this.window = owWindowScreenAnchorOptions.window;
    this.anchorType = owWindowScreenAnchorOptions.anchorType;
    this.isTopMost = owWindowScreenAnchorOptions.isTopMost;
    this.owWindowListener = new OWWindowsListener(this);
  }

  // ---------------------------------------------------------------------------
  public async start() {
    this.stop();
    await this.setMainMonitorInfo();

    await this.restoreWindow();
    await this.repositionWindow();
    this.owWindowListener.start();
  }

  // ---------------------------------------------------------------------------
  public stop() {
    this.owWindowListener.stop();
    this.window.close();
  }

  // ---------------------------------------------------------------------------
  private async setMainMonitorInfo() {
    const displays = await OWUtils.getMonitorsList();
    // return primary add method
    this.mainMonitor = displays.find((monitor) => monitor.is_primary === true);
  }

  // ---------------------------------------------------------------------------
  private async restoreWindow() {
    await this.window.restore();
  }

  // ---------------------------------------------------------------------------
  private async repositionWindow() {
    const windowInfo = await this.window.obtain();
    const { width, height } = this.mainMonitor;
    await this.changeWindowPosition(windowInfo, width, height);

    if (this.isTopMost) {
      await this.window.setTopmost();
    }
  }

  // ---------------------------------------------------------------------------
  private async changeWindowPosition(
    windowInfo: overwolf.windows.WindowInfo,
    monitorWidth: number,
    monitorHeight: number,
  ) {
    let top: number;
    let left: number;

    switch (this.anchorType) {
      case OWWindowAnchorTypes.topRight:
        top = 0;
        left = monitorWidth - windowInfo.width;
        break;
      case OWWindowAnchorTypes.topLeft:
        top = 0;
        left = 0;
        break;
      case OWWindowAnchorTypes.bottomLeft:
        top = monitorHeight - windowInfo.height;
        left = 0;
        break;
      case OWWindowAnchorTypes.bottomRight:
        top = monitorHeight - windowInfo.height;
        left = monitorWidth - windowInfo.width;
        break;
    }

    await this.window.changePosition(left, top);
  }

  // ---------------------------------------------------------------------------
  // OWWindowsListenerDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onScreenPropertyChanged(event: onScreenPropertyChangedEvent) {
    if (event?.monitor?.id !== this.mainMonitorId) {
      return;
    }

    this.repositionWindow();
  }
}
